import { allows } from "@giftforward/entitlements";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useRef } from "react";
import { PillToggle } from "@/components/campaignBuilder/design/PillToggle";
import { TokenDropdown } from "@/components/campaignBuilder/design/TokenDropdown";
import { availableTokens, EMAIL_FIELD_LIMITS } from "@/constants/campaignBuilder";
import { type UnboxingSettings, useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { useMyEntitlements } from "@/hooks/useMyEntitlements";
import type { TokenKey } from "@/types/types";
import { isAnonymousLinks } from "@/utils/campaignMode-helper";
import { insertAtCursor } from "@/utils/messagePanel-helper";
import { CustomVideoUnlocked } from "./CustomVideoUnlocked";
import { FieldGroup } from "./FieldGroup";
import { FieldLabel } from "./FieldLabel";
import { LockedBadge } from "./LockedBadge";
import { SkipUnboxingUnlocked } from "./SkipUnboxingUnlocked";
import { TokenCheckbox } from "./TokenCheckbox";

interface UnboxingCustomizeDrawerProps {
  open: boolean;
  onClose: () => void;
  settings: UnboxingSettings;
  onSettingsChange: (v: Partial<UnboxingSettings>) => void;
}

export default function UnboxingCustomizeDrawer({
  open,
  onClose,
  settings,
  onSettingsChange,
}: UnboxingCustomizeDrawerProps) {
  const { ent } = useMyEntitlements();
  const { notificationMode, linkToList } = useCampaignBuilder();
  const canSkipUnboxing = !!ent && allows(ent, "unboxing", "customize", "skip");
  const canUseCustomVideo = !!ent && allows(ent, "unboxing", "customize", "custom_video");
  const giftMessageRef = useRef<HTMLTextAreaElement>(null);
  // Only ANONYMOUS links (manual "No, just set a quantity" Path A, or any universal) can't resolve
  // @FirstName / @LastName in the gift message — drop them from the tag dropdown there. A manual "link
  // to a list" campaign (Path B) has named recipients like gfg, so it keeps them. See availableTokens.
  const giftMsgTokens = availableTokens(isAnonymousLinks(notificationMode, linkToList));

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape" && open) onClose();
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  function toggleToken(key: TokenKey) {
    const next: UnboxingSettings = { ...settings, [key]: !settings[key] };

    // Signature is mutually exclusive with sender name tokens
    if (key === "tokenSignature" && next.tokenSignature) {
      next.tokenFirstName = false;
      next.tokenLastName = false;
      next.tokenCompany = false;
    } else if (key !== "tokenSignature" && next[key]) {
      next.tokenSignature = false;
    }

    // Last Name requires First Name
    if (key === "tokenFirstName" && !next.tokenFirstName) next.tokenLastName = false;
    if (key === "tokenLastName" && next.tokenLastName) next.tokenFirstName = true;

    // At least one token must stay on
    const anyOn =
      next.tokenFirstName || next.tokenLastName || next.tokenCompany || next.tokenSignature;
    if (!anyOn) return;

    onSettingsChange(next);
  }

  return (
    <div
      className="flex flex-col bg-white fixed top-0 right-0 bottom-0"
      style={{
        width: 380,
        minWidth: 380,
        maxWidth: 380,
        flex: "none",
        transform: open ? "translateX(0)" : "translateX(100%)",
        transition: "transform 0.28s cubic-bezier(0.22, 0.61, 0.36, 1)",
        borderLeft: "1px solid var(--color-card-border)",
        boxShadow: "-12px 0 32px rgba(26, 22, 18, 0.10)",
        pointerEvents: open ? "auto" : "none",
        zIndex: 200,
        willChange: "transform",
        marginTop: "76px",
      }}
      aria-hidden={!open}
    >
      {open && (
        <div className="flex flex-col h-full">
          {/* Header */}
          <div
            className="flex items-center justify-between shrink-0"
            style={{
              padding: "14px 20px 12px",
              borderBottom: "1px solid var(--color-card-border)",
            }}
          >
            <h3
              style={{
                fontFamily: "var(--font-lato)",
                fontSize: "14px",
                fontWeight: 700,
                color: "var(--color-dark-dust)",
                textTransform: "uppercase",
                letterSpacing: "1.5px",
                margin: 0,
                lineHeight: 1,
              }}
            >
              Customize the Unboxing
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="flex items-center justify-center text-graphite hover:bg-wax hover:text-rodeo-black"
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: 0,
                transition: "all 0.15s",
              }}
              aria-label="Close customize panel"
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </button>
          </div>

          {/* Single "Settings" tab — flex-1 matching email drawer's config-tab */}
          <div
            className="flex shrink-0"
            style={{ borderBottom: "1px solid var(--color-card-border)", background: "white" }}
          >
            <div
              className="flex-1 font-lato text-[13px] font-semibold text-center border-b-2 select-none"
              style={{
                padding: "14px 16px",
                color: "var(--color-rodeo-black)",
                borderBottomColor: "var(--color-rodeo-black)",
              }}
            >
              Settings
            </div>
          </div>

          {/* Panel body */}
          <div className="flex-1 overflow-y-auto" style={{ padding: 20 }}>
            {/* ── Title Personalization ── */}
            <FieldGroup>
              <FieldLabel>Title Personalization</FieldLabel>
              <div className="flex flex-wrap gap-x-5 gap-y-2.5">
                <TokenCheckbox
                  label="First Name"
                  checked={settings.tokenFirstName}
                  onToggle={() => toggleToken("tokenFirstName")}
                />
                <TokenCheckbox
                  label="Last Name"
                  checked={settings.tokenLastName}
                  onToggle={() => toggleToken("tokenLastName")}
                />
                <TokenCheckbox
                  label="from Company"
                  checked={settings.tokenCompany}
                  onToggle={() => toggleToken("tokenCompany")}
                />
                <TokenCheckbox
                  label="Signature"
                  checked={settings.tokenSignature}
                  onToggle={() => toggleToken("tokenSignature")}
                />
              </div>
            </FieldGroup>

            {/* ── Gift Message ── */}
            <FieldGroup>
              <FieldLabel>
                <div className="flex items-center gap-3">
                  Gift Message
                  {settings.showGiftMessage && (
                    <TokenDropdown
                      tokens={giftMsgTokens}
                      onInsert={(token) => {
                        const el = giftMessageRef.current;
                        if (el)
                          insertAtCursor(
                            el,
                            token,
                            (v) => onSettingsChange({ giftMessageText: v }),
                            EMAIL_FIELD_LIMITS.message,
                          );
                      }}
                    />
                  )}
                </div>
                <PillToggle
                  checked={settings.showGiftMessage}
                  onChange={(v) => onSettingsChange({ showGiftMessage: v })}
                  aria-label="Show gift message"
                />
              </FieldLabel>
              {settings.showGiftMessage && (
                <>
                  <textarea
                    ref={giftMessageRef}
                    value={settings.giftMessageText}
                    onChange={(e) => onSettingsChange({ giftMessageText: e.target.value })}
                    maxLength={EMAIL_FIELD_LIMITS.message}
                    rows={4}
                    placeholder="This message appears when recipients tap Read Message on the unboxing screen."
                    className="font-lato w-full"
                    style={{
                      padding: "8px 10px",
                      border: "1px solid var(--color-smoke)",
                      borderRadius: "var(--radius-sm)",
                      fontSize: 13,
                      color: "var(--color-rodeo-black)",
                      lineHeight: 1.5,
                      resize: "vertical",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                  <div
                    className="flex justify-end font-lato"
                    style={{ marginTop: 4, fontSize: 11, color: "var(--color-graphite)" }}
                  >
                    {settings.giftMessageText.length} / {EMAIL_FIELD_LIMITS.message}
                  </div>
                </>
              )}
            </FieldGroup>

            {/* ── Skip Unboxing — plan-gated ── */}
            <FieldGroup>
              {canSkipUnboxing ? (
                <SkipUnboxingUnlocked
                  checked={settings.skipUnboxing}
                  onChange={(v) => onSettingsChange({ skipUnboxing: v })}
                />
              ) : (
                <FieldLabel>
                  Skip Unboxing
                  <LockedBadge tier="upgrade" />
                </FieldLabel>
              )}
            </FieldGroup>

            {/* ── Custom Video — plan-gated ── */}
            <FieldGroup>
              {canUseCustomVideo ? (
                <>
                  <FieldLabel>Custom Video</FieldLabel>
                  <CustomVideoUnlocked
                    videoUrl={settings.customVideoUrl}
                    onChange={(url) => onSettingsChange({ customVideoUrl: url })}
                  />
                </>
              ) : (
                <FieldLabel>
                  Custom Video
                  <LockedBadge tier="enterprise" />
                </FieldLabel>
              )}
            </FieldGroup>
          </div>
        </div>
      )}
    </div>
  );
}
